import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  QueryList,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HorizontalSliderItemComponent } from '../horizontal-slider-item/horizontal-slider-item.component';

const DRAG_DISTANCE_THRESHOLD = 25;

@Component({
  selector: 'pxw-horizontal-slider',
  templateUrl: './horizontal-slider.component.html',
  styleUrls: ['./horizontal-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.dragging]': 'isDragging()',
  },
})
export class HorizontalSliderComponent implements AfterViewInit {
  @ContentChildren(HorizontalSliderItemComponent)
  children: QueryList<HorizontalSliderItemComponent>;

  private rootEl: ElementRef<HTMLElement> = inject(ElementRef);
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);

  protected isDragging = signal(false);

  private dragInitiated = false;
  private startX = 0;
  private startScrollLeft = 0;

  ngAfterViewInit(): void {
    this.children.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.rootEl.nativeElement) {
        this.rootEl.nativeElement.scrollLeft = 0;
      }
      this.dragInitiated = false;
      this.startX = 0;
    });

    const window = this.document.defaultView;

    if (isPlatformBrowser(this.platformId) && window?.matchMedia('(pointer: fine)').matches) {
      this.initMouseDrag();
    }
  }

  scrollToIndex(index: number, animated: boolean) {
    const sliderItem = this.children.toArray()?.[index];

    if (sliderItem) {
      this.scrollToItem(sliderItem, animated);
    }
  }

  scrollToId(id: string, animated: boolean) {
    const sliderItem = this.children.toArray().find(item => item.id === id);

    if (sliderItem) {
      this.scrollToItem(sliderItem, animated);
    }
  }

  private initMouseDrag(): void {
    const el = this.rootEl.nativeElement;

    el.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0 || el.scrollWidth <= el.clientWidth) {
        return;
      }

      this.dragInitiated = true;
      this.startX = e.clientX;
      this.startScrollLeft = el.scrollLeft;

      this.document.addEventListener('mousemove', this.onMouseMove);
      this.document.addEventListener('mouseup', this.onMouseUp);
    });

    el.addEventListener(
      'click',
      e => {
        if (this.dragInitiated && Math.abs(e.clientX - this.startX) >= DRAG_DISTANCE_THRESHOLD) {
          e.preventDefault();
          e.stopPropagation();
        }
        this.dragInitiated = false;
      },
      true,
    );

    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('mousemove', this.onMouseMove);
      this.document.removeEventListener('mouseup', this.onMouseUp);
    });
  }

  private onMouseMove = (e: MouseEvent) => {
    if (!this.isDragging()) {
      this.isDragging.set(true);
    }

    this.rootEl.nativeElement.scrollLeft = this.startScrollLeft - (e.clientX - this.startX);
  };

  private onMouseUp = () => {
    this.document.removeEventListener('mousemove', this.onMouseMove);
    this.document.removeEventListener('mouseup', this.onMouseUp);

    this.isDragging.set(false);
    setTimeout(() => {
      this.dragInitiated = false;
    });
  };

  private scrollToItem(sliderItem: HorizontalSliderItemComponent, animated: boolean): void {
    const left = this.children.first === sliderItem ? 0 : sliderItem.offsetLeft;

    this.rootEl.nativeElement.scrollTo({
      left,
      behavior: <ScrollBehavior>(animated ? 'smooth' : 'instant'),
    });
  }
}
